//================= [JS] ======================================
import {UserNotificationApi} from '@/common/api/notification/UserNotificationApi';
import {loadNotifications} from '@/composables/layout/baseLayout';
import {Events} from '@/common/utils/Events';
import {useEventBus} from '@simpleforyou/client-core';
import {useI18n} from 'vue-i18n';
import {useStore} from '@/common/store/store';
import {fetchEventSource} from '@microsoft/fetch-event-source';

// A single shared connection across the app.
let controller: AbortController | null = null;
// Consecutive failed opens; drives the backoff, reset by a successful open.
let failures = 0;
// A token renewal in flight; the next reconnect waits for it.
let renewing: Promise<string | null> | null = null;

const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 60000;

class UnauthorizedError extends Error {}

/**
 * T00P67IRXAIS — Wait before the next reconnect: 1 s, 2 s, 4 s … capped at 60 s.
 * Without it fetch-event-source retries every second, forever — an idle tab
 * with an expired token sent ~50,000 requests in one night.
 */
export const retryDelay = (attempt: number): number => Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);

/**
 * @param refresh renews the access token (useAuth().refresh); resolves null
 *   when the session is gone. The stream bypasses the ApiClient and with it the
 *   ApiClient's 401 handling, so it has to renew on its own.
 */
export function useNotificationStream(refresh?: () => Promise<string | null>) {
  const eventBus = useEventBus();
  const {t} = useI18n();
  const store = useStore();

  // OIDC — der SSE-Stream wird via @microsoft/fetch-event-source geöffnet (nicht der
  // native EventSource, der keinen Authorization-Header setzen kann). Das OIDC-Access-
  // Token liegt im Store und wird als Bearer mitgesendet; der `fetch`-Wrapper liest es
  // bei jedem (Re-)Connect frisch, damit ein zwischenzeitlich erneuertes Token greift.
  const connect = (): void => {
    if (controller) {
      return;
    }
    controller = new AbortController();
    fetchEventSource(UserNotificationApi.stream(), {
      signal: controller.signal,
      // Beim Tab-Wechsel offen halten (Default bricht bei hidden ab).
      openWhenHidden: true,
      fetch: async (input, init) => {
        if (renewing) {
          await renewing;
        }
        return fetch(input, {
          ...init,
          credentials: 'include',
          headers: {...(init?.headers ?? {}), Authorization: `Bearer ${store.accessToken ?? ''}`},
        });
      },
      async onopen(response) {
        if (response.ok && response.headers.get('content-type')?.startsWith('text/event-stream')) {
          failures = 0;
          return;
        }
        throw response.status === 401 ? new UnauthorizedError() : new Error(`stream open failed: ${response.status}`);
      },
      onmessage(event) {
        let payload: {type?: string; text?: string; payload?: unknown} = {};
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        // E00U6EKA5EGC — call signalling rides on the same stream; the call component listens.
        if (payload.type === 'call-signal') {
          eventBus.emit(Events.CALL_SIGNAL, payload.payload);
          return;
        }
        if (payload.type !== 'notification') {
          return; // ignore heartbeats
        }
        // Refresh badge/list (cache=false in loadNotifications) and toast.
        loadNotifications();
        eventBus.emit(Events.NOTIFY, payload.text || t('navigation.notification.new'));
      },
      // Not throwing keeps fetch-event-source reconnecting (with a fresh token
      // via the fetch wrapper); the return value is the delay before it does.
      onerror(error) {
        if (error instanceof UnauthorizedError) {
          if (!refresh) {
            throw error;
          }
          // Renew once; no token means the session is gone — stop knocking.
          renewing ??= refresh()
            .then((token) => {
              if (!token) {
                disconnect();
              }
              return token;
            })
            .catch(() => {
              disconnect();
              return null;
            })
            .finally(() => {
              renewing = null;
            });
        }
        return retryDelay(failures++);
      },
    }).catch(() => {
      // aborted (disconnect) oder finaler Abbruch — Verbindung ist zu.
      controller = null;
    });
  };

  function disconnect(): void {
    failures = 0;
    if (controller) {
      controller.abort();
      controller = null;
    }
  }

  return {connect, disconnect};
}
